import type React from 'react';
import dayjs from 'dayjs';
import {TFunction} from 'i18next';
import {ErrorType, FastAPIServiceError, FieldError, PhoneDetails} from '../types';

type NetworkInformation = {
  effectiveType?: string;
};

function getErrorMessage(error: ErrorType, t?: TFunction): string {
  let message = '';

  if (!error) {
    return message;
  }

  if (typeof error === 'string') {
    return error;
  }

  if ('details' in error && typeof error.details === 'string') {
    return error?.details;
  }
  if (isObject(error) && 'errors' in error && error?.errors?.length) {
    const nonFieldErrors = 'non_field_errors';
    Object.values(error.errors).forEach((err: FieldError) => {
      const translatedFieldName = t ? t(err.field) : err.field;
      message += ` ${
        err?.field && err?.field !== nonFieldErrors ? `${translatedFieldName}: ` : ''
      } ${err.message};`;
    });
  } else if ('message' in error && error?.message) {
    message = error.message;
  } else {
    if (typeof error === 'object') {
      Object.values(error).forEach(err => {
        message += ` ${err}\n`;
      });
    }
  }

  if (typeof message === 'object') {
    return '';
  }

  if (message?.startsWith('Unexpected JSON')) {
    return 'Server error.';
  }

  return message;
}

function getFastAPIErrorMessage(error: FastAPIServiceError): string {
  if (Array.isArray(error.detail)) {
    return error.detail[0]?.msg ?? error.message ?? 'An unexpected error occurred';
  }
  return error.detail ?? error.message ?? 'An unexpected error occurred';
}

function checkIsInternetConnectionFast(): boolean {
  try {
    const deviceNetworkConnection = (
      navigator as Navigator & {connection?: NetworkInformation}
    )?.connection;

    return ['4g', '5g'].includes(deviceNetworkConnection?.effectiveType ?? '');
  } catch {
    return false;
  }
}

function getCorrectUrl(url: string) {
  if (url.includes('http')) {
    try {
      const urlObj = new URL(url);
      return urlObj.search;
    } catch {
      return url;
    }
  } else {
    return url;
  }
}

function getSearchParamFromUrl<T = string>(param = '', url = window.location.search) {
  if (!url) return null;

  const searchString = getCorrectUrl(url);
  const searchParams = new URLSearchParams(searchString);
  return searchParams.get(param) as T | null;
}

const getRegExpFromString = (value: string) => {
  return new RegExp(value || '');
};

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

const isObject = (value: unknown) => {
  return value !== null && typeof value === 'object';
};

function parseDate(date: number | string | Date): Date {
  if (date === null) return new Date(NaN);
  if (date instanceof Date) return new Date(date);
  if (typeof date === 'string') {
    try {
      const res = dayjs(date).toDate();
      if (dayjs(res).isValid()) {
        return res;
      }
    } catch (e) {
      console.error(e);
    }
  }

  return new Date(date);
}

type RefItem<T> =
  | ((element: T | null) => void)
  | React.RefObject<T | null>
  | null
  | undefined;

function mergeRefs<T>(...refs: RefItem<T>[]): (element: T | null) => void {
  return (element: T | null) => {
    refs.forEach(ref => {
      if (!ref) return;
      if (typeof ref === 'function') ref(element);
      else (ref as React.MutableRefObject<T | null>).current = element;
    });
  };
}

const getPhoneNumberString = (phone?: PhoneDetails | string | null) => {
  if (typeof phone === 'object') {
    return phone?.code ? `${phone?.code}${phone?.number}` : '';
  }

  return phone;
};

const isBlobInstance = (value: unknown) => {
  return value instanceof Blob;
};

function getFileSizeMB(file?: File | Blob) {
  if (!file) return;
  return file.size / Math.pow(1024, 2);
}

/** Backend ids arrive dashed in some payloads and bare in others. */
const sanitizeStringFromDashes = (value?: string | null) =>
  value?.replaceAll('-', '') ?? '';

export {
  getErrorMessage,
  getFastAPIErrorMessage,
  checkIsInternetConnectionFast,
  getSearchParamFromUrl,
  getRegExpFromString,
  isBrowser,
  isObject,
  parseDate,
  mergeRefs,
  getPhoneNumberString,
  isBlobInstance,
  getFileSizeMB,
  sanitizeStringFromDashes,
};
export type {RefItem};
